import { Component, EventEmitter, Injector, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Checklist, SaveChecklistStatusResource } from 'src/app/components/checklists/checklists';
import { ChecklistsService } from 'src/app/components/checklists/checklists.service';
import { WFSectionLocalResource, WFStepLocalResource, WFTaskSignatureResource, WfActionLocalResource, WfTaskLocalResource } from 'src/app/components/workflow/workflow';
import { WorkFlowService } from 'src/app/components/workflow/workflow.service';
import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { ChecklistsUpdate } from 'src/app/components/checklists/store/checklists/checklists.action';
import { ClBaseComponent } from '../cl-base/cl-base.component';
import { FormType, FormStatusEnum, Action } from 'src/app/common/enumerations/enumerations';
import { ScheduleStatusEnum } from 'src/app/components/schedules/models/enums';
import { Schedule } from 'src/app/components/schedules/models/schedule';
import { ScheduleService } from 'src/app/components/schedules/services/schedule.service';
import { Role } from 'src/app/components/catalogs/roles/services/role';
import { YesNoDialogComponent } from '../../yes-no-dialog/yes-no-dialog.component';
import { KEService } from 'src/app/components/kenable/ke.service';
import { KEResource, SaveKEStatusResource } from 'src/app/components/kenable/ke';
import { AbsiResource } from 'src/app/components/absi/absi';
import { DocumentType } from 'src/app/services/documents/documents';
@Component({
  selector: 'cl-close-action',
  templateUrl: './cl-close-action.component.html',
  styleUrls: ['./cl-close-action.component.scss']
})
export class ClCloseActionComponent extends ClBaseComponent implements OnInit, OnChanges, OnDestroy {

  @Input() currentChecklist?: Checklist | KEResource | AbsiResource | null;
  @Output() closed = new EventEmitter<boolean>();

  public name?: string | null;

  allRoles: Role[] = [];
  roles = [];
  public showRoles!: Role[];

  schedule?: Schedule | null;
  schedules!: Schedule[];
  schedules$!: Observable<Schedule[]>;
  schedulesSubs!: Subscription;

  public hidden!: boolean;

  wfTaskSignature: WFTaskSignatureResource = new WFTaskSignatureResource();

  fullRoleName!: string;

  isChecked?: boolean;
  toggleLabel?: string;

  constructor(
    public _wf: WorkFlowService,
    public _ke: KEService,
    public _checklist: ChecklistsService,
    private serviceSchedule: ScheduleService,
    protected override injector: Injector
  ) {
    super(injector);
  }

  override ngOnDestroy(): void {
    this.schedulesSubs?.unsubscribe();
  }

  ngOnInit() {
    this.toggleLabel = this.getMessage('ScheduleCloseLabel').description;
  }

  ngOnChanges(changes: SimpleChanges) {
    this.loadSchedules();
    this.refresh();
  }

  /**
   * Resolve and assign the most appropriate schedule for the active checklist.
   * Prefer store-backed matches with fallbacks for closed-state handling and direct API lookup, then sync component/checklist state and trigger downstream schedule checks.
   * Mutates `this.schedule`, updates `currentChecklist.scheduleId`, may show a warning alert, and subscribes to schedules store updates.
   */
  loadSchedules() {
    // Snapshot the checklist we are working with for this call
    const checklist = this.currentChecklist as Checklist;

    // Reset the selected schedule; we will determine it again below
    this.schedule = null;

    // Only do work if we have *some* key that can link a checklist to a schedule
    // - scheduleId: direct relationship
    // - procedureID: indirect relationship (via procedureMasterID below)
    if (checklist?.scheduleId || checklist?.checklistTemplate?.procedureID) {

      // Stream of schedules from the store (NgRx selector)
      this.schedules$ = this.store.select(state => state.Schedules.data);

      // Subscribe to schedules updates (NOTE: ensure you unsubscribe on destroy)
      this.schedulesSubs = this.schedules$.subscribe(async data => {

        // Clean store data:
        // 1) remove schedules marked as "removed"
        // 2) sort by createdOn so "latest" is at the end (used by .at(-1))
        this.schedules = data
          .filter(x => x.statusId != ScheduleStatusEnum.remove)
          .sort((a, b) => this.utils.sort(a.createdOn, b.createdOn));

        // If the checklist form is canceled, we skip schedule resolution entirely
        // (but you still call checkSchedule() after this block)
        if (checklist && checklist.checklistStatus?.formStatusID != FormStatusEnum.Canceled) {

          // Primary lookup:
          // - Find schedule by scheduleId
          // - If checklist is NOT closed, prefer a schedule that is NOT completed
          let schedule = this.schedules.find(s =>
            s.id == checklist.scheduleId &&
            s.statusId != ScheduleStatusEnum.completed &&
            checklist.status != FormStatusEnum.Closed
          );

          // If checklist IS closed, allow completed schedules (exact ID match only)
          if (checklist.status == FormStatusEnum.Closed) {
            schedule = this.schedules.find(s => s.id == checklist.scheduleId);
          }

          // If still not found (and not closed), fall back to the most recent schedule
          // that matches the checklist’s procedureMasterID (take last after sorting)
          if (checklist.status != FormStatusEnum.Closed && !schedule) {
            schedule = this.schedules
              .filter(s => s.procedureMasterID == checklist.checklistTemplate?.procedureMasterID)
              .at(-1);
          }

          // Final fallback: ask the backend by ID (covers cases where store is stale / not loaded)
          if (!schedule && checklist.scheduleId) {
            schedule = await this.getScheduleByID(checklist.scheduleId ?? 0);
          }

          // Persist chosen schedule to component state
          this.schedule = schedule;
          (this.currentChecklist as Checklist).scheduleId = this.schedule?.id;
          // Warn the user if we still couldn’t find anything (but don’t warn for closed checklists)
          if (!this.schedule && checklist.status != FormStatusEnum.Closed) {
            this.alert.warning('No Schedule found for this checklist!');
          }

          // Post-resolution hook: update UI/flags based on the selected schedule
          this.checkSchedule();
        }
      });
    }
  }


  async getScheduleByID(id: number) {
    return await this.serviceSchedule.getById(id).toPromise();
  }

  checkSchedule() {
    if (!this.valueString) {
      if (this.schedule) this.isChecked = true;
      else this.isChecked = false;
    }
    else this.isChecked = this.utils.JSONparse(this.valueString).checked;
  }

  buttonDisabled() {
    return this.disabled || !this.hasRoles(this.roleIds) || this.disabledClose();
  }

  /**
   * Block close actions when the current item is not ready and requires strict signature/flow rules.
   * Apply the restriction to signature-required checklist templates and KE items identified by the KE serial pattern.
   * @returns Whether the close action should be disabled.
   */
  disabledClose() {
    // Disable close when not ready AND the current item matches guarded checklist/KE scenarios.
    if (!this._checklist.readyToClose() && (this.currentChecklist instanceof Checklist && this.currentChecklist?.checklistTemplate?.templateType?.allSignaturesRequired || this.currentChecklist instanceof KEResource && this.currentChecklist?.serialNo?.includes('KE-'))) {
      return true;
    }
    // Allow close in all other cases.
    return false;
  }

  refresh() {
    if (!this.value) {
      this.label = 'Close';
      this.name = null;
      this.date = null;
    }
    else {
      this.name = this.user?.name;
      this.date = this.date;
    }
    this.checkSchedule();
  }

  override get labelHtml(): string {
    const text = this.question?.trim() ? this.question : this.title;
    const question = this.stripStrong(text);
    const flattened = this.flattenNestedB(question ?? '');
    return flattened.replace(/<b>/gi, '<b class="blue">');
  }


  /**
   * Start the close flow when the action is allowed and the user confirms completion.
   * Capture signer metadata, emit immediate close state when no checklist is loaded, and delegate final closing to checklist-type specific handlers.
   */
  async click(): Promise<void> {
    // Exit early when already set, or when in editor/builder contexts.
    if (this.value || this.editor || this.builder) {
      return;
    }

    // Ask for explicit user confirmation before applying close actions.
    const confirmed = await this.confirmFinish();

    if (!confirmed) {
      return;
    }

    // Stamp current user and close timestamp for UI/state tracking.
    this.name = this.getCurrentUser()?.name;
    this.date = new Date();

    // Without a checklist payload, only emit the toggle state outward.
    if (!this.currentChecklist) {
      this.closed.emit(this.isChecked);
      return;
    }

    try {
      // Route to document-specific close behavior.
      await this.closeCurrentChecklist();
    } catch (error) {
      // Preserve current behavior by logging unexpected close failures.
      console.error(error);
    }
  }

  /**
   * Prompt the user to confirm finishing the current item.
   * @returns Whether the user confirmed the finish action.
   */
  private async confirmFinish(): Promise<boolean> {
    // Open a simple yes/no confirmation dialog using localized message content.
    const dialogRef = this.dialog.open(YesNoDialogComponent, {
      width: '400px',
      data: {
        message: this.getMessage('finish_message')?.description
      }
    });

    // Convert dialog close stream into a boolean confirmation result.
    return !!await firstValueFrom(dialogRef.afterClosed());
  }

  /**
   * Resolve workflow context and execute the proper close branch for the current document type.
   * Emit close state directly when workflow metadata is unavailable.
   */
  private async closeCurrentChecklist(): Promise<void> {
    // Resolve the active workflow task and its approve action used by close operations.
    const wfTaskLocal = this.getCurrentWfTaskLocal();
    const wfActionApprove = wfTaskLocal?.wfActionLocals?.find(
      (a: WfActionLocalResource) => a.action === Action.Approve
    );

    // If workflow linkage is missing, emit state and skip backend close orchestration.
    if (!this.currentChecklist?.wfTableID) {
      this.closed.emit(this.isChecked);
      return;
    }

    // Load document type to decide which close strategy to execute.
    const documentType = await firstValueFrom(
      this._wf.getDocumentTypeByWFTableID(this.currentChecklist.wfTableID)
    );

    // Dispatch to form-specific close handlers.
    switch (documentType?.id) {
      case FormType.KE:
        await this.closeKeyEnable(wfTaskLocal, wfActionApprove);
        break;

      case FormType.ABSI:
        this.markAsClosedLocally();
        break;

      default:
        await this.closeChecklist(wfTaskLocal, wfActionApprove);
        break;
    }
  }

  /**
   * Locate the workflow task local that matches the component task id within the current checklist hierarchy.
   * @returns The matching workflow task local, if present.
   */
  private getCurrentWfTaskLocal(): WfTaskLocalResource | undefined {
    // Find the section that contains a step/task matching the active task id.
    const section = this.currentChecklist?.wfTable?.wfTableLocal?.wfSectionLocals
      ?.find((s: WFSectionLocalResource) =>
        s?.wfStepLocals?.some(step =>
          step.wfTaskLocals?.some(task => task.id === this.taskID)
        )
      );

    // Narrow to the exact step containing the active task.
    const step = section?.wfStepLocals?.find((s: WFStepLocalResource) =>
      s.wfTaskLocals?.some(task => task.id === this.taskID)
    );

    // Return the concrete task local used by downstream workflow actions.
    return step?.wfTaskLocals?.find(
      (task: WfTaskLocalResource) => task.id === this.taskID
    );
  }

  /**
   * Attempt a standard KE close signature flow and fall back to force-close when signing fails.
   * @param wfTaskLocal Workflow task context containing executable task ids.
   * @param wfActionApprove Approve action metadata required for signing.
   */
  private async closeKeyEnable(
    wfTaskLocal?: WfTaskLocalResource,
    wfActionApprove?: WfActionLocalResource
  ): Promise<void> {
    // Extract required workflow ids for the KE sign call.
    const taskId = wfTaskLocal?.wfTasks?.[0]?.id;
    const actionId = wfActionApprove?.id;

    // Stop when required workflow identifiers are missing.
    if (!taskId || !actionId) {
      return;
    }

    // Execute KE sign; success closes locally, failure offers force-close path.
    const result = await firstValueFrom(this._ke.sign(taskId, actionId));

    if (result) {
      this.markAsClosedLocally();
    } else {
      this.forceClose(FormType.KE, wfTaskLocal);
    }
  }

  /**
   * Persist a checklist close status through the checklist service and synchronize local/store state.
   * @param wfTaskLocal Workflow task context used to populate the status payload.
   * @param wfActionApprove Approve action metadata required to validate close prerequisites.
   */
  private async closeChecklist(
    wfTaskLocal?: WfTaskLocalResource,
    wfActionApprove?: WfActionLocalResource
  ): Promise<void> {
    // Extract workflow ids required to submit close status.
    const taskId = wfTaskLocal?.wfTasks?.[0]?.id;
    const actionId = wfActionApprove?.id;

    // Abort when workflow context is incomplete.
    if (!taskId || !actionId || !wfTaskLocal) {
      return;
    }

    // Treat the active item as checklist for checklist-specific status updates.
    const checklist = this.currentChecklist as Checklist;

    // Resolve template-specific closed status, with enum fallback.
    const closedStatus = checklist.checklistTemplate?.documentType?.checklistExecStatuses
      ?.find(s => s.formStatusID === FormStatusEnum.Closed);

    // Build close payload, optionally carrying schedule linkage when toggle is enabled.
    const saveChecklistStatus: SaveChecklistStatusResource = {
      id: checklist.id,
      checklistStatusID: closedStatus?.id,
      status: FormStatusEnum.Closed,
      scheduleID: this.isChecked ? checklist.scheduleId : undefined,
      wfTaskLocalID: wfTaskLocal.id
    };

    // Sync user context and notify parent before remote persistence.
    this.user = this.currentUser;
    this.closed.emit(this.isChecked);

    // Persist closed status and receive the latest checklist snapshot.
    const result = await firstValueFrom(
      this._checklist.putStatus(saveChecklistStatus)
    );

    // Update local state from backend response.
    this.currentChecklist = result;
    this.value = true;

    // Notify user with the closed-form message including serial number.
    this.alert.message('FormClosed', [
      {
        placeholder: '{serialNo}',
        value: this.currentChecklist?.serialNo
      }
    ]);

    // Keep NgRx store in sync with the updated checklist.
    if (result) {
      this.store.dispatch(new ChecklistsUpdate(result.id, result));
    }
  }

  /**
   * Mark the item as closed in local component state and notify listeners.
   */
  private markAsClosedLocally(): void {
    // Apply local closed flag and emit the current schedule-close toggle value.
    this.value = true;
    this.closed.emit(this.isChecked);
  }

  async forceClose(
    formType: FormType,
    wfTaskLocal?: WfTaskLocalResource
  ): Promise<void> {
    const confirmed = await this.confirmForceClose();

    switch (formType) {
      case FormType.KE:
        if (confirmed) {
          await this.forceCloseKeyEnable();
        } else {
          await this.rollbackKeyEnableApproval(wfTaskLocal);
        }
        break;
    }
  }

  private async confirmForceClose(): Promise<boolean> {
    const dialogRef = this.dialog.open(YesNoDialogComponent, {
      width: '500px',
      data: {
        message: this.getMessage('ForceCloseChecklist')?.description,
        icon: 'stop'
      }
    });

    return !!await firstValueFrom(dialogRef.afterClosed());
  }

  private async forceCloseKeyEnable(): Promise<void> {
    const saveKEStatus: SaveKEStatusResource = {
      id: this.currentChecklist!.id,
      status: FormStatusEnum.Closed
    };

    await firstValueFrom(this._ke.putStatus(saveKEStatus));

    this.value = true;
    this.closed.emit(this.isChecked);
  }

  private async rollbackKeyEnableApproval(
    wfTaskLocal?: WfTaskLocalResource
  ): Promise<void> {
    const taskId = wfTaskLocal?.wfTasks?.[0]?.id;

    const unapproveActionId = wfTaskLocal?.wfActionLocals?.find(
      action => action.action === Action.Unapprove
    )?.id;

    if (!taskId || !unapproveActionId) {
      this.value = false;
      return;
    }

    await firstValueFrom(
      this._ke.sign(taskId, unapproveActionId)
    );

    this.value = false;
  }

  toggleChanged() {
    if (this.isChecked) {
      this.alert.message('ScheduleCloseOn');
    }
    else {
      this.alert.message('ScheduleCloseOff');
    }
    this.valueString = this.utils.JSONstringify({ checked: this.isChecked });
    this.changed.emit(this.valueString);
  }

}
